<script lang="ts">
  /**
   * The settings page (R3.2 §3): one row per notebook — `name`, `@tag`, a
   * native export anchor, and a `[Delete…]` button. Reached from the
   * list header's `[Settings]` button only (no hotkey); the keyboard layer is
   * dead here, so there are no key handlers at all. Reads the local list
   * projection; authors nothing — the delete and the import POST belong to
   * the container, via `onDeleteRequest` and `onImport`.
   */
  import Trash from 'lucide/dist/esm/icons/trash.mjs';
  import Download from 'lucide/dist/esm/icons/download.mjs';
  import FileUp from 'lucide/dist/esm/icons/file-up.mjs';
  import Pencil from 'lucide/dist/esm/icons/pencil.mjs';
  import CircleUser from 'lucide/dist/esm/icons/circle-user.mjs';
  import LogOut from 'lucide/dist/esm/icons/log-out.mjs';
  import { ANONYMOUS_OWNER_ID, type NotebookSummary } from '../storage/operations';
  import type { Account } from '../storage/account';
  import type { Theme } from '../storage/theme';
  import Icon from './Icon.svelte';

  interface Props {
    notebooks: NotebookSummary[];
    onBack: () => void;
    onDeleteRequest: (notebookId: string) => void;
    /** Hands the picked bundle file to the host, which POSTs it (IDEAS 10).
     * Optional so the page renders standalone. */
    onImport?: (file: File) => void;
    /** Opens the edit modal for the notebook. Optional, same reason. */
    onEditRequest?: (notebookId: string) => void;
    /**
     * The signed-in account this browser acts as, or null — the anonymous app,
     * whose settings page has nothing account-shaped on it. It decides the one
     * account row: an admin gets the `[ Admin ]` entry to the account page,
     * anybody else gets identity and sign-out right here, because the account
     * page has nothing left for them.
     */
    account?: Account | null;
    /** Opens the account page. Rendered for admins only. */
    onAdmin?: () => void;
    /** Ends the login. Rendered for non-admins only; the host owns the rest. */
    onSignOut?: () => void;
    /**
     * Opens the account-deletion confirm modal (R5.5). Lives beside the
     * member's sign-out — the admin's delete entry is on the account page,
     * where their sign-out lives. Optional so the page renders standalone.
     */
    onDeleteAccount?: () => void;
    /**
     * The owner this browser is acting as (R7) — the host's, because the export
     * anchor is a RELAY request and the auth gate binds the path's owner
     * segment to the login. Optional so the page renders standalone, where the
     * anonymous owner is the only owner there can be.
     */
    ownerId?: string;
    /** The theme preference — auto (follows the OS), light, or dark. */
    theme?: Theme;
    /** Commits a new theme preference; the host persists it and applies it. */
    onThemeChange?: (next: Theme) => void;
  }

  let {
    notebooks,
    onBack,
    onDeleteRequest,
    onImport,
    onEditRequest,
    account = null,
    onAdmin,
    onSignOut,
    onDeleteAccount,
    ownerId = ANONYMOUS_OWNER_ID,
    theme = 'auto',
    onThemeChange,
  }: Props = $props();

  let picker: HTMLInputElement | null = $state(null);

  /**
   * R5.4: the takeout-shaped sibling of the per-row export — one bundle
   * carrying every notebook, ids repeated in list order. Null when the list is
   * empty: the server rejects an empty selection, so the anchor has no zero
   * state.
   */
  const exportAllHref = $derived(
    notebooks.length === 0
      ? null
      : `/api/owners/${ownerId}/export?${new URLSearchParams(
          notebooks.map((notebook) => ['notebook_id', notebook.notebook_id]),
        )}`,
  );

  function pickBundle(event: Event): void {
    const input = event.currentTarget as HTMLInputElement;
    const file = input.files?.[0];
    if (file) onImport?.(file);
    // Picking the SAME file twice must re-fire.
    input.value = '';
  }
</script>

<div class="settings-view" data-testid="settings-view">
  <header class="settings-header">
    <h1 tabindex="-1">Settings</h1>
    <!-- The one account row: an admin's way into the account page, where
         their sign-out lives. Anybody else gets sign-out at the foot of this
         page instead — the account page has nothing left for them — and the
         anonymous app has nothing account-shaped at all. -->
    {#if account?.is_admin && onAdmin}
      <button class="action" type="button" data-testid="settings-admin" onclick={onAdmin}>
        <Icon node={CircleUser} size={14} />
        <span>[ Admin ]</span>
      </button>
    {/if}
    <!-- R5.4: the account-level export — the whole notebook set in one
         download, beside the import that is its reverse. The owner is the
         ACTING one, the same value the per-row export anchors use (R7). -->
    {#if exportAllHref}
      <a
        class="action"
        data-testid="settings-export-all"
        href={exportAllHref}
        download="all-notebooks.af1-export.json"
      >
        <Icon node={Download} size={14} />
        <span>Export all</span>
      </a>
    {/if}
    <!-- IDEAS 10: the import surface. The keyboard layer is dead on this view;
         the button is pointer/Tab reachable only. -->
    <button class="action" type="button" data-testid="notebook-import-open" onclick={() => picker?.click()}>
      <Icon node={FileUp} size={14} />
      <span>Import</span>
    </button>
    <input
      type="file"
      accept=".json,application/json"
      hidden
      bind:this={picker}
      data-testid="notebook-import"
      onchange={pickBundle}
    />
    <button
      class="action"
      type="button"
      data-testid="settings-back"
      onclick={onBack}
    >
      [ Back to list ]
    </button>
  </header>

  <!-- Theme: auto follows the OS, light/dark force a surface. Auto by
       default; the host persists the choice and applies it as `data-theme`
       on `html`, so it takes effect everywhere, including standalone. -->
  <div class="settings-row" data-testid="settings-theme">
    <span class="settings-name">Theme</span>
    <select
      class="settings-select"
      bind:value={theme}
      onchange={() => onThemeChange?.(theme as Theme)}
      aria-label="Theme"
    >
      <option value="auto">Auto</option>
      <option value="light">Light</option>
      <option value="dark">Dark</option>
    </select>
  </div>

  <ul class="settings-rows">
    {#each notebooks as notebook (notebook.notebook_id)}
      <li class="settings-row" data-testid="settings-row">
        <span class="settings-name">{notebook.name}</span>
        <span class="settings-tag">@{notebook.tag}</span>
        <!-- The §17.1 export surface verbatim — a native download, no JS. The
             owner is the ACTING one: in account mode the gate refuses the
             anonymous path with a 401, so a hardcoded sentinel is a dead link
             for every signed-in user. -->
        <a
          class="action"
          data-testid="settings-export"
          href={`/api/owners/${ownerId}/export?notebook_id=${notebook.notebook_id}`}
          download={`${notebook.name}.af1-export.json`}
        >
          <Icon node={Download} size={13} />
          <span>Export</span>
        </a>
        <button
          class="action"
          type="button"
          data-testid="settings-edit"
          title="Edit {notebook.name}"
          onclick={() => onEditRequest?.(notebook.notebook_id)}
        >
          <Icon node={Pencil} size={13} />
          <span>Edit</span>
        </button>
        <button
          class="action"
          type="button"
          data-testid="settings-delete"
          onclick={() => onDeleteRequest(notebook.notebook_id)}
        >
          <Icon node={Trash} size={13} />
          <span>[ Delete… ]</span>
        </button>
      </li>
    {/each}
  </ul>

  {#if notebooks.length === 0}
    <p class="settings-empty">No notebooks yet.</p>
  {/if}

  <!-- The non-admin's sign-out: the account page is admin-only, so this is
       the only account surface a member has. Who they are, then the way out —
       the host owns the whole sign-out sequence, as it does for the panel. -->
  {#if account && !account.is_admin && onSignOut}
    <footer class="settings-signout" data-testid="settings-signout">
      <p class="settings-identity" data-testid="settings-identity">
        <Icon node={CircleUser} size={14} />
        <span>Signed in as {account.email}</span>
        {#if account.name}
          <span class="settings-signout-name">{account.name}</span>
        {/if}
      </p>
      <button class="action" type="button" data-testid="settings-signout-button" onclick={onSignOut}>
        <Icon node={LogOut} size={14} />
        <span>[ Sign out ]</span>
      </button>
      {#if onDeleteAccount}
        <button
          class="action"
          type="button"
          data-testid="settings-delete-account"
          onclick={onDeleteAccount}
        >
          <Icon node={Trash} size={14} />
          <span>[ Delete account… ]</span>
        </button>
      {/if}
    </footer>
  {/if}
</div>

<style>
  .settings-view {
    width: min(100%, 60em);
    margin: 0 auto;
    padding: 2rem 1rem;
  }

  .settings-header {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 1rem;
  }

  h1 {
    font-family: inherit;
    font-size: 1.4rem;
    font-weight: normal;
  }

  ul {
    list-style: none;
    margin: 1.25rem 0 0;
    padding: 0;
  }

  .settings-row {
    display: flex;
    align-items: baseline;
    gap: 0.75rem;
    padding: 0.6rem 0;
    border-bottom: 1px solid color-mix(in srgb, var(--ink-muted) 30%, transparent);
  }

  .settings-name {
    font-size: var(--hand-size);
  }

  .settings-tag {
    font-family: var(--font-mono, monospace);
    font-size: 10px;
    color: var(--accent);
  }

  .settings-select {
    font: inherit;
    font-size: var(--hand-size);
    background: var(--paper);
    color: var(--ink);
    border: 1px solid color-mix(in srgb, var(--ink) 50%, transparent);
    /* A control boundary is held to 3:1 (WCAG 1.4.11). 50% ink is the cheapest
       color-mix that clears it in BOTH themes (3.03 light / 4.09 dark) — the
       shared value for every boxed input, so do not dial it back down. */
    border-radius: 3px;
    padding: 1px 6px;
  }

  .settings-select option {
    background: var(--paper);
    color: var(--ink);
  }

  /* Both actions share the row end: the auto margin sits on the first
     action (Export) so Delete lands directly beside it. */
  .settings-row a.action {
    margin-left: auto;
  }

  .settings-empty {
    color: var(--ink-muted);
  }

  .settings-signout {
    margin-top: 2rem;
    padding-top: 1rem;
    border-top: 1px solid color-mix(in srgb, var(--ink-muted) 30%, transparent);
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 1rem;
  }

  .settings-identity {
    display: flex;
    align-items: baseline;
    gap: 0.5rem;
    margin: 0;
  }

  .settings-signout-name {
    color: var(--ink-muted);
  }
</style>
